import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/EmptyState';
import { APPOINTMENT_STATES } from '../utils/mockData';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Users,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { API } from '@/lib/api';

const PERIODS = [
  { key: 'dia',    label: 'Día' },
  { key: 'semana', label: 'Semana' },
  { key: 'mes',    label: 'Mes' },
];

const STATUS_ACTIONS = {
  confirmada: [
    { next: 'en_sala',    label: 'Pasar a Sala',  variant: 'amber' },
    { next: 'cancelada',  label: 'Cancelar',       variant: 'rose' },
    { next: 'no_asistio', label: 'No Asistió',     variant: 'slate' },
  ],
  en_sala: [
    { next: 'atendido',   label: 'Atendido',       variant: 'emerald' },
    { next: 'cancelada',  label: 'Cancelar',        variant: 'rose' },
  ],
  atendido:   [],
  cancelada:  [],
  no_asistio: [],
};

const VARIANT_CLASSES = {
  amber:   'border-amber-300 text-amber-700 hover:bg-amber-50',
  rose:    'border-rose-300 text-rose-700 hover:bg-rose-50',
  emerald: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50',
  slate:   'border-slate-300 text-slate-600 hover:bg-slate-50',
};

/* ── Date helpers ─────────────────────────────────────────────── */
const fmt = (d) => d.toISOString().slice(0, 10);

function getRangePeriod(period, base) {
  const d = new Date(base);
  if (period === 'dia') {
    return { inicio: fmt(d), fin: fmt(d) };
  }
  if (period === 'semana') {
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday-based
    const mon = new Date(d); mon.setDate(d.getDate() + diff);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { inicio: fmt(mon), fin: fmt(sun) };
  }
  // mes
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { inicio: fmt(start), fin: fmt(end) };
}

function stepDate(period, base, dir) {
  const d = new Date(base);
  if (period === 'dia')    d.setDate(d.getDate() + dir);
  if (period === 'semana') d.setDate(d.getDate() + dir * 7);
  if (period === 'mes')    d.setMonth(d.getMonth() + dir);
  return d;
}

function periodLabel(period, base) {
  const { inicio, fin } = getRangePeriod(period, base);
  if (period === 'dia') {
    return new Date(inicio + 'T12:00:00').toLocaleDateString('es-ES', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  }
  if (period === 'semana') {
    return `${new Date(inicio + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} – ${new Date(fin + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return new Date(inicio + 'T12:00:00').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
}

/* ── KPI Card ─────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, colorClass }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', colorClass)}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold leading-tight">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Appointment Card ─────────────────────────────────────────── */
function AptCard({ apt, onStatusChange }) {
  const state  = APPOINTMENT_STATES[apt.estado] || APPOINTMENT_STATES.confirmada;
  const actions = STATUS_ACTIONS[apt.estado] || [];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{apt.paciente_nombre}</p>
            <p className="text-xs text-muted-foreground">{apt.hora_inicio} · {apt.doctor_nombre}</p>
            {apt.motivo && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">{apt.motivo}</p>
            )}
          </div>
          <Badge variant="outline" className={cn('shrink-0 text-xs', state.color)}>
            {state.label}
          </Badge>
        </div>

        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border">
            {actions.map(({ next, label, variant }) => (
              <Button
                key={next}
                size="sm"
                variant="outline"
                className={cn('h-7 text-xs px-2', VARIANT_CLASSES[variant])}
                onClick={() => onStatusChange(apt.id, next)}
              >
                {label}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Main Page ────────────────────────────────────────────────── */
export const RecepcionistaDashboard = () => {
  const [period,        setPeriod]        = useState('dia');
  const [baseDate,      setBaseDate]      = useState(new Date());
  const [doctors,       setDoctors]       = useState([]);
  const [selectedDoc,   setSelectedDoc]   = useState(null); // null = todos
  const [appointments,  setAppointments]  = useState([]);
  const [loading,       setLoading]       = useState(false);

  // El token lo inyecta axios globalmente (AuthContext).

  /* Load doctors once */
  useEffect(() => {
    axios.get(`${API}/doctors`)
      .then(r => setDoctors(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Load appointments whenever period/date/doctor changes */
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    const { inicio, fin } = getRangePeriod(period, baseDate);
    const params = { fecha_inicio: inicio, fecha_fin: fin, limit: 500 };
    if (selectedDoc) params.doctor_id = selectedDoc;
    try {
      const r = await axios.get(`${API}/appointments`, { params });
      setAppointments(r.data);
    } catch {
      toast.error('Error al cargar citas');
    } finally {
      setLoading(false);
    }
  }, [period, baseDate, selectedDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  /* Status change */
  const handleStatusChange = async (aptId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${aptId}/status`, null, {
        params: { estado: newStatus },
      });
      toast.success(`Estado actualizado a "${APPOINTMENT_STATES[newStatus]?.label}"`);
      loadAppointments();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  /* KPIs */
  const kpis = {
    total:       appointments.length,
    confirmadas: appointments.filter(a => a.estado === 'confirmada').length,
    en_sala:     appointments.filter(a => a.estado === 'en_sala').length,
    atendidas:   appointments.filter(a => a.estado === 'atendido').length,
    canceladas:  appointments.filter(a => a.estado === 'cancelada' || a.estado === 'no_asistio').length,
  };

  /* Grouped appointments by date (for semana/mes views) */
  const grouped = appointments.reduce((acc, apt) => {
    const key = apt.fecha;
    if (!acc[key]) acc[key] = [];
    acc[key].push(apt);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort();

  const showGrouped = period !== 'dia';

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Panel de Recepción</h1>
            <p className="text-sm text-muted-foreground capitalize">{periodLabel(period, baseDate)}</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadAppointments} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
            Actualizar
          </Button>
        </div>

        {/* Period selector + navigation */}
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map(p => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? 'default' : 'outline'}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
          <div className="flex items-center gap-1 ml-auto">
            <Button variant="ghost" size="icon" onClick={() => setBaseDate(d => stepDate(period, d, -1))} aria-label="Periodo anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setBaseDate(new Date())}>
              Hoy
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setBaseDate(d => stepDate(period, d, 1))} aria-label="Periodo siguiente">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Doctor filter chips */}
        {doctors.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedDoc(null)}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                selectedDoc === null
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              )}
            >
              Todos
            </button>
            {doctors.map(d => (
              <button
                key={d.id}
                onClick={() => setSelectedDoc(selectedDoc === d.id ? null : d.id)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  selectedDoc === d.id
                    ? 'text-white border-transparent'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
                style={selectedDoc === d.id ? { backgroundColor: d.color || '#0ea5e9' } : {}}
              >
                {d.nombre}
              </button>
            ))}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard icon={CalendarDays} label="Total"       value={kpis.total}       colorClass="bg-blue-100 text-blue-700" />
          <KpiCard icon={CheckCircle2} label="Confirmadas" value={kpis.confirmadas} colorClass="bg-blue-100 text-blue-700" />
          <KpiCard icon={Users}        label="En Sala"     value={kpis.en_sala}     colorClass="bg-amber-100 text-amber-700" />
          <KpiCard icon={Clock}        label="Atendidas"   value={kpis.atendidas}   colorClass="bg-emerald-100 text-emerald-700" />
          <KpiCard icon={XCircle}      label="Ausencias"   value={kpis.canceladas}  colorClass="bg-rose-100 text-rose-700" />
        </div>

        {/* Appointment list */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <Card>
            <EmptyState
              icon={CalendarDays}
              title="Sin citas en este período"
              description="Cambia el período o la fecha para ver otras citas."
            />
          </Card>
        ) : showGrouped ? (
          <div className="space-y-4">
            {sortedDates.map(date => (
              <div key={date}>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2 capitalize">
                  {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', {
                    weekday: 'long', day: 'numeric', month: 'long',
                  })}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grouped[date]
                    .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
                    .map(apt => (
                      <AptCard key={apt.id} apt={apt} onStatusChange={handleStatusChange} />
                    ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <CardHeader className="px-0 pt-0">
              <CardTitle className="text-base">
                {appointments.length} {appointments.length === 1 ? 'cita' : 'citas'}
              </CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...appointments]
                .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
                .map(apt => (
                  <AptCard key={apt.id} apt={apt} onStatusChange={handleStatusChange} />
                ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
